import type { FailedToParseImportModel } from '@nx/api';
import * as ImportShared from '@nx/import-shared';
import { isFetchBaseQueryError } from '@nx/state';
import { isNullish } from '@nx/stdlib';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

import AwsCloudBucketLogo from '../assets/aws-s3-cloud-bucket-logo.svg';
import AzureCloudBucketLogo from '../assets/azure-cloud-bucket-logo.svg';
import BigQueryLogo from '../assets/bigquery-logo.svg';
import CSVIcon from '../assets/csv-icon.svg';
import defaultLogo from '../assets/data-sources-splash.svg';
import DataBricksLogo from '../assets/databricks-logo.svg';
import GcsCloudBucketLogo from '../assets/gcs-cloud-bucket-logo.svg';
import MySQLLogo from '../assets/mysql-logo.svg';
import OracleLogo from '../assets/oracle-logo.svg';
import PostgreSQLLogo from '../assets/postgresql-logo.svg';
import SnowflakeLogo from '../assets/snowflake-logo.svg';
import SQLServerLogo from '../assets/sql-server-logo.svg';
import { FIELD_IS_REQUIRED_TEXT } from '../constants';

const DATA_SOURCE_ICON: Record<string, string> = {
  snowflake: SnowflakeLogo,
  postgresql: PostgreSQLLogo,
  sqlserver: SQLServerLogo,
  oracle: OracleLogo,
  mysql: MySQLLogo,
  bigquery: BigQueryLogo,
  csv: CSVIcon,
  databricks: DataBricksLogo,
  awss3: AwsCloudBucketLogo,
  azureblobsdatalakestorage: AzureCloudBucketLogo,
  googlecloudstorage: GcsCloudBucketLogo,
};

const DEFAULT_DB_ICON = defaultLogo;

export const getDataSourceIcon = (groupName: string) => {
  const normalizedGroupName = groupName.toLowerCase().replace(/[ &]/g, '');
  return DATA_SOURCE_ICON[normalizedGroupName] ?? DEFAULT_DB_ICON;
};

export const getDataSourceTypeGroupLabelFromDefinitions = (
  type: string,
  dataSourceDefinitions?: ImportShared.DataSourceDefinition[],
) => {
  const dataSource = dataSourceDefinitions?.find((d) => d.type === type);
  return dataSource?.groupLabel;
};

// For some reason get runtime error if this function is in  '@nx/api' so putting it here instead
export const isFailedToParseImportModel = (model: unknown): model is FailedToParseImportModel => {
  return (
    typeof model === 'object' &&
    model !== null &&
    'id' in model &&
    'version' in model &&
    'createdAt' in model &&
    'updatedAt' in model &&
    'dataModel' in model &&
    'visualisation' in model &&
    model.dataModel === null &&
    model.visualisation === null
  );
};

export type ImportBackedServiceError = {
  data: { message?: string; code?: string };
  status: string | number;
};
export const isImportBackedServiceError = (error: unknown): error is ImportBackedServiceError => {
  if (typeof error !== 'object' || isNullish(error)) {
    return false;
  }
  if ('data' in error && typeof error.data === 'object' && !isNullish(error.data)) {
    const { data } = error;
    if ('message' in data && typeof data.message !== 'string') {
      return false;
    }
    if ('code' in data && typeof data.code !== 'string') {
      return false;
    }
    return true;
  }
  return false;
};

export type ImportApiError = {
  message: string;
  status: number | string;
};
const isImportApiError = (error: unknown): error is ImportApiError => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    'status' in error &&
    typeof error.status === 'number' &&
    typeof error.message === 'string'
  );
};

const UNDEFINED_ERROR_MESSAGE = 'Unknown error';
const UNDEFINED_STATUS = 'Status code not available';

export const createImportApiError = (message: string, status?: number | string): ImportApiError => {
  return { message, status: status ?? UNDEFINED_STATUS };
};

export const getImportApiErrorMessage = (error: unknown, overrideStatus?: number | string): string => {
  let status: number | string = UNDEFINED_STATUS;
  let errorMessage = UNDEFINED_ERROR_MESSAGE;

  if (typeof error === 'string') {
    return error;
  }

  if (isImportBackedServiceError(error)) {
    errorMessage = error.data.message ?? UNDEFINED_ERROR_MESSAGE;
    status = error.status;
  } else if (isImportApiError(error)) {
    errorMessage = error.message;
    status = error.status;
  } else if (isFetchBaseQueryError(error)) {
    const typedError: FetchBaseQueryError = error;
    status = error.status;
    if (typedError.status === 'FETCH_ERROR') {
      errorMessage = typedError.error;
    } else if (typedError.status === 'PARSING_ERROR') {
      errorMessage = `${typedError.error}: ${typedError.data}`;
    } else if (typedError.status === 'TIMEOUT_ERROR') {
      errorMessage = typedError.error;
    } else if (typedError.status === 'CUSTOM_ERROR') {
      errorMessage = typedError.error;
      status = UNDEFINED_STATUS;
    } else if (typeof typedError.status === 'number') {
      errorMessage = JSON.stringify(typedError.data);
    }
  } else if (typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string') {
    errorMessage = error.message;
  }

  if (!isNullish(overrideStatus)) {
    status = overrideStatus;
  }

  return `[${status}] ${errorMessage}`;
};

export const getErrorMessageFromApiHook = (e: unknown): { message: string | undefined; isAbortError: boolean } => {
  const message: string = getImportApiErrorMessage(e);
  let isAbortError = false;
  // Check if it is an abort error
  if (typeof e === 'object' && e !== null && 'name' in e && 'message' in e) {
    if (!isNullish(e.name) && typeof e.name === 'string' && e.name === 'AbortError') {
      isAbortError = true;
    }
  }

  return { message, isAbortError };
};

export const getAllFieldsInDefinition = (dataSourceConfig?: ImportShared.DataSourceDefinition) => {
  return dataSourceConfig?.sections.flatMap((section) => section.fields) ?? [];
};

const getNumberValue = (value: ImportShared.FormFieldValue | ImportShared.FieldValue | undefined) => {
  return !isNaN(Number(value)) ? Number(value) : 0;
};

export const getNumberFormField = (
  field: ImportShared.FormField | ImportShared.FieldDefinition,
  value: ImportShared.FormFieldValue | undefined,
): ImportShared.NumberFormField => {
  const numberValue = getNumberValue(value);
  return {
    ...field,
    type: ImportShared.INTEGER_CONFIGURATION_TYPE,
    value: numberValue,
  };
};

export const getNumberField = (
  field: ImportShared.Field | ImportShared.FieldDefinition,
  value: ImportShared.FieldValue | undefined,
): ImportShared.NumberField => {
  const numberValue = getNumberValue(value);
  return {
    name: field.name,
    type: ImportShared.INTEGER_CONFIGURATION_TYPE,
    value: numberValue,
  };
};

export const getBooleanValue = (value: ImportShared.FormFieldValue | ImportShared.FieldValue | undefined) => {
  return value === true;
};

export const getBooleanFormField = (
  field: ImportShared.FormField | ImportShared.FieldDefinition,
  value: ImportShared.FormFieldValue | undefined,
): ImportShared.BooleanFormField => {
  return {
    ...field,
    type: ImportShared.BOOLEAN_CONFIGURATION_TYPE,
    value: getBooleanValue(value),
  };
};

export const getBooleanField = (
  field: ImportShared.Field | ImportShared.FieldDefinition,
  value: ImportShared.FieldValue | undefined,
): ImportShared.BooleanField => {
  return {
    name: field.name,
    type: ImportShared.BOOLEAN_CONFIGURATION_TYPE,
    value: getBooleanValue(value),
  };
};

export const getStringValue = (value: ImportShared.FormFieldValue | ImportShared.FieldValue | undefined) => {
  return value?.toString() ?? '';
};

export const getTextFormField = (
  field: ImportShared.FormField | ImportShared.FieldDefinition,
  value: ImportShared.FormFieldValue | undefined,
): ImportShared.TextFormField => {
  return {
    ...field,
    type: ImportShared.TEXT_CONFIGURATION_TYPE,
    value: getStringValue(value),
  };
};

export const getTextField = (
  field: ImportShared.Field | ImportShared.FieldDefinition,
  value: ImportShared.FieldValue | undefined,
): ImportShared.TextField => {
  return {
    name: field.name,
    type: ImportShared.TEXT_CONFIGURATION_TYPE,
    value: getStringValue(value),
  };
};

export const getUserNameFormField = (
  field: ImportShared.FormField | ImportShared.FieldDefinition,
  value: ImportShared.FormFieldValue | undefined,
): ImportShared.TextFormField => {
  return {
    ...field,
    type: ImportShared.USERNAME_CONFIGURATION_TYPE,
    value: getStringValue(value),
  };
};

export const getUserNameField = (
  field: ImportShared.Field | ImportShared.FieldDefinition,
  value: ImportShared.FieldValue | undefined,
): ImportShared.TextField => {
  return {
    name: field.name,
    type: ImportShared.USERNAME_CONFIGURATION_TYPE,
    value: getStringValue(value),
  };
};

export const getPasswordFormField = (
  field: ImportShared.FormField | ImportShared.FieldDefinition,
  value: ImportShared.FormFieldValue | undefined,
): ImportShared.TextFormField => {
  return {
    ...field,
    type: ImportShared.PASSWORD_CONFIGURATION_TYPE,
    value: getStringValue(value),
  };
};

export const getPasswordField = (
  field: ImportShared.Field | ImportShared.FieldDefinition,
  value: ImportShared.FieldValue | undefined,
): ImportShared.TextField => {
  return {
    name: field.name,
    type: ImportShared.PASSWORD_CONFIGURATION_TYPE,
    value: getStringValue(value),
  };
};

export const getMultilinePasswordFormField = (
  field: ImportShared.PrimitiveFormField | ImportShared.FieldDefinition,
  value: ImportShared.PrimitiveFormFieldValue | undefined,
): ImportShared.PrimitiveFormField => {
  return {
    ...field,
    type: ImportShared.PASSWORD_MULTILINE_CONFIGURATION_TYPE,
    value: getStringValue(value),
  };
};

export const validateField = (
  value: ImportShared.PrimitiveFormFieldValue | undefined,
  configurationField: ImportShared.PrimitiveFieldDefinition,
) => {
  const { validation, type, required } = configurationField;

  if (type === 'integer') {
    if (value !== undefined && value !== '' && isNaN(Number(value))) {
      return 'Value must be a number';
    } else if (!Number.isInteger(Number(value))) {
      return 'Value must be an integer';
    }
  }

  if (required && (isNullish(value) || value === '')) {
    return FIELD_IS_REQUIRED_TEXT;
  }

  let error = null;
  if (!isNullish(validation)) {
    validation.forEach((rule) => {
      switch (rule.type) {
        case 'minimum': {
          const numberValue = Number(value);
          if (!isNaN(numberValue)) {
            if (numberValue < rule.value) {
              error = `Value must be at least ${rule.value}`;
            }
          }
          break;
        }
        case 'maximum': {
          const numberValue = Number(value);
          if (!isNaN(numberValue)) {
            if (numberValue > rule.value) {
              error = `Value must be at most ${rule.value}`;
            }
          }
          break;
        }
        case 'minLength':
          if (typeof value === 'string' && value.length < rule.value) {
            error = `Value must have at least ${rule.value} number of characters`;
          }
          break;
        case 'maxLength':
          if (typeof value === 'string' && value.length > rule.value) {
            error = `Value must have at most ${rule.value} number of characters`;
          }
          break;
        default:
          break;
      }
    });
  }
  return error;
};

export const validateArrayField = (
  arrayValue: Record<string, ImportShared.PrimitiveFormFieldValue>[],
  fields: ImportShared.PrimitiveFieldDefinition[],
) => {
  const errors: ImportShared.PrimitiveError[] = [];

  arrayValue.forEach((value) => {
    const keys = Object.keys(value);
    const error: ImportShared.PrimitiveError = {};
    keys.forEach((key) => {
      const fieldElement = fields.find((f) => f.name === key);
      if (fieldElement) {
        error[key] = validateField(value[key], fieldElement);
      }
    });
    errors.push(error);
  });
  return errors;
};

export const getValidFieldValue = (
  field: ImportShared.Field | ImportShared.FieldDefinition,
  value: ImportShared.FormFieldValue | undefined,
) => {
  if (field.type === ImportShared.INTEGER_CONFIGURATION_TYPE) {
    return getNumberValue(value);
  } else if (field.type === ImportShared.BOOLEAN_CONFIGURATION_TYPE) {
    return getBooleanValue(value);
  }
  return getStringValue(value);
};

export const getValidPrimitiveField = (
  field: ImportShared.PrimitiveFormField,
  value: ImportShared.PrimitiveFormFieldValue | undefined,
): ImportShared.PrimitiveFormField | undefined => {
  switch (field.type) {
    case ImportShared.INTEGER_CONFIGURATION_TYPE:
      return getNumberFormField(field, value);
    case ImportShared.BOOLEAN_CONFIGURATION_TYPE:
      return getBooleanFormField(field, value);
    case ImportShared.TEXT_CONFIGURATION_TYPE:
      return getTextFormField(field, value);
    case ImportShared.USERNAME_CONFIGURATION_TYPE:
      return getUserNameFormField(field, value);
    case ImportShared.PASSWORD_CONFIGURATION_TYPE:
      return getPasswordFormField(field, value);
    default:
      return undefined;
  }
};

export const getArrayFormFieldFromDataSourceConfig = (
  fieldName: string,
  dataSourceConfig: ImportShared.DataSourceFullConfig,
): ImportShared.ArrayFormField | undefined => {
  const arrayField = dataSourceConfig.fields.find((f) => f.name === fieldName);
  if (!isNullish(arrayField) && ImportShared.isArrayFormField(arrayField)) {
    return arrayField;
  }
  return undefined;
};

export const getArrayFieldFromDataSourceConfig = (
  fieldName: string,
  dataSourceConfig: ImportShared.SavedDataSourceConfig,
): ImportShared.ArrayField | undefined => {
  const arrayField = dataSourceConfig.fields.find((f) => f.name === fieldName);
  if (!isNullish(arrayField) && ImportShared.isArrayField(arrayField)) {
    return arrayField;
  }
  return undefined;
};

export const getArrayFieldValue = (fields: ImportShared.ArrayFieldValueElement[]) => {
  const arrayFieldValueInArray = fields.map((field) => {
    const keys = Object.keys(field);
    const arrayFieldValue: Record<string, ImportShared.PrimitiveFormFieldValue> = {};
    keys.forEach((key) => {
      const fieldElement = field[key];
      if (fieldElement) {
        arrayFieldValue[key] = getValidFieldValue(fieldElement, fieldElement.value);
      }
    });
    return arrayFieldValue;
  });
  return arrayFieldValueInArray;
};
export const getArrayFieldValueFromDataSourceConfig = (
  fieldName: string,
  dataSourceConfig: ImportShared.DataSourceConfig,
): Record<string, ImportShared.PrimitiveFormFieldValue>[] => {
  const field = dataSourceConfig.fields.find((f) => f.name === fieldName);
  if (!isNullish(field) && ImportShared.isArrayField(field)) {
    const arrayFieldValue = getArrayFieldValue(field.value);
    return arrayFieldValue;
  }
  return [];
};

export const getFieldValueFromDataSourceConfig = (
  fieldName: string,
  dataSourceConfig: ImportShared.DataSourceConfig,
): ImportShared.PrimitiveFormFieldValue => {
  const field = dataSourceConfig.fields.find((f) => f.name === fieldName);
  if (!isNullish(field) && !ImportShared.isArrayField(field)) {
    return getValidFieldValue(field, field.value);
  }
  return '';
};

export const getPrimitiveFormFieldValueFromDataSourceConfig = (
  fieldName: string,
  dataSourceConfig: ImportShared.DataSourceConfig,
): ImportShared.PrimitiveFormFieldValue => {
  const field = dataSourceConfig.fields.find((f) => f.name === fieldName);
  if (!isNullish(field)) {
    if (ImportShared.isArrayField(field)) {
      return '';
    }
    return getValidFieldValue(field, field.value);
  }
  return '';
};

export const nameField: ImportShared.FieldDefinition = {
  label: 'Name',
  name: 'name',
  type: 'text',
  description: 'The name to identify your data source by in the data sources list',
  required: true,
  validation: [],
};

export const getFieldValueErrors = (
  dataSourceConfig: ImportShared.DataSourceConfig,
  dataSourceDefinition: ImportShared.DataSourceDefinition,
) => {
  const newErrors: ImportShared.DataSourceValidationErrors = {};

  getAllFieldsInDefinition(dataSourceDefinition).forEach((field) => {
    if (ImportShared.isArrayFieldDefinition(field)) {
      const arrayValue = getArrayFieldValueFromDataSourceConfig(field.name, dataSourceConfig);
      const error = validateArrayField(arrayValue, field.fields);
      newErrors[field.name] = error;
      return;
    }
    const value = getFieldValueFromDataSourceConfig(field.name, dataSourceConfig);
    const error = validateField(value, field);
    newErrors[field.name] = error;
  });

  const nameError = validateField(dataSourceConfig.name, nameField);
  newErrors[nameField.name] = nameError;

  return newErrors;
};

export const getPrimitiveFormFieldWithCorrectType = (
  field: ImportShared.PrimitiveFormField | ImportShared.FieldDefinition,
  value: ImportShared.PrimitiveFormFieldValue,
): ImportShared.PrimitiveFormField => {
  switch (field.type) {
    case ImportShared.INTEGER_CONFIGURATION_TYPE:
      return getNumberFormField(field, value);
    case ImportShared.BOOLEAN_CONFIGURATION_TYPE:
      return getBooleanFormField(field, value);
    case ImportShared.USERNAME_CONFIGURATION_TYPE:
      return getUserNameFormField(field, value);
    case ImportShared.PASSWORD_CONFIGURATION_TYPE:
      return getPasswordFormField(field, value);
    case ImportShared.PASSWORD_MULTILINE_CONFIGURATION_TYPE:
      return getMultilinePasswordFormField(field, value);
    default:
      return getTextFormField(field, value);
  }
};

export const getPrimitiveFieldWithCorrectType = (
  field: ImportShared.PrimitiveField | ImportShared.PrimitiveFieldDefinition,
  value: ImportShared.PrimitiveFieldValue | undefined,
): ImportShared.PrimitiveField => {
  let primitiveField: ImportShared.PrimitiveField;
  switch (field.type) {
    case ImportShared.INTEGER_CONFIGURATION_TYPE:
      primitiveField = getNumberField(field, value);
      break;
    case ImportShared.BOOLEAN_CONFIGURATION_TYPE:
      primitiveField = getBooleanField(field, value);
      break;
    case ImportShared.USERNAME_CONFIGURATION_TYPE:
      primitiveField = getUserNameField(field, value);
      break;
    case ImportShared.PASSWORD_CONFIGURATION_TYPE:
      primitiveField = getPasswordField(field, value);
      break;
    default:
      primitiveField = getTextField(field, value);
      break;
  }
  return primitiveField;
};

export const getArrayField = (
  field: ImportShared.ArrayFieldDefinition,
  value: ImportShared.ApiArrayFieldValueElement[] | undefined,
): ImportShared.ArrayField | undefined => {
  const arrayFormFieldValue: ImportShared.ArrayFieldValueElement[] = [];
  value?.forEach((v) => {
    const fieldsRecord: ImportShared.ArrayFieldValueElement = {};
    const keys = Object.keys(v);
    keys.forEach((key) => {
      const fieldValue = v[key]?.value;
      if (isNullish(fieldValue)) {
        return;
      }
      const primitiveField = field.fields.find((f) => f.name === key);
      if (isNullish(primitiveField)) {
        return;
      }
      const f = getPrimitiveFieldWithCorrectType(primitiveField, fieldValue);
      fieldsRecord[key] = f;
    });
    arrayFormFieldValue.push(fieldsRecord);
  });
  return {
    name: field.name,
    type: ImportShared.ARRAY_CONFIGURATION_TYPE,
    value: arrayFormFieldValue,
  };
};

export const getFieldWithCorrectType = (
  field: ImportShared.FieldDefinition,
  value: ImportShared.FormFieldValue | ImportShared.ApiArrayFieldValueElement[] | undefined,
): ImportShared.Field | undefined => {
  if (ImportShared.isArrayFieldDefinition(field) && Array.isArray(value)) {
    return getArrayField(field, value);
  } else if (!ImportShared.isArrayFieldDefinition(field) && !Array.isArray(value)) {
    return getPrimitiveFieldWithCorrectType(field, value);
  }
  return undefined;
};

const getDefaultFieldValueFromDefinition = (field: ImportShared.PrimitiveFieldDefinition) => {
  return getValidFieldValue(field, field.default);
};

export const getFormFieldWithValueFromDefinition = (
  field: ImportShared.PrimitiveFieldDefinition,
): ImportShared.PrimitiveFormField => {
  const value = getDefaultFieldValueFromDefinition(field);
  return getPrimitiveFormFieldWithCorrectType(field, value);
};

export const getDefaultArrayFieldValue = (
  fieldDefinition: ImportShared.ArrayFieldDefinition,
): ImportShared.ArrayFormFieldValueElement => {
  const defaultArrayFieldValue: ImportShared.ArrayFormFieldValueElement = {};
  fieldDefinition.fields.forEach((field) => {
    defaultArrayFieldValue[field.name] = getFormFieldWithValueFromDefinition(field);
  });
  return defaultArrayFieldValue;
};

export const getDefaultArrayFormFieldFromDefinition = (
  field: ImportShared.ArrayFieldDefinition,
): ImportShared.ArrayFormField => {
  const arrayFormFieldValue: ImportShared.ArrayFormFieldValue = field.required
    ? [getDefaultArrayFieldValue(field)]
    : [];
  const arrayFormField: ImportShared.ArrayFormField = {
    name: field.name,
    label: field.label,
    description: field.description,
    required: field.required,
    type: ImportShared.ARRAY_CONFIGURATION_TYPE,
    value: arrayFormFieldValue,
  };
  return arrayFormField;
};
